import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { FiRefreshCw, FiX, FiCheck } from "react-icons/fi";
import { fetchRoles } from "../../../redux/actions/role-management/role/roleActions";
import { fetchPermissions } from "../../../redux/actions/role-management/permission/permissionActions";
import {
  assignRolePermissions,
  fetchRolePermissions,
} from "../../../redux/actions/role-management/assignPermission/assignPermissionActions";

const AssignPermission = () => {
  const dispatch = useDispatch();
  const { roles } = useSelector((state) => state.role || { roles: [] });
  const { permissions } = useSelector(
    (state) => state.permission || { permissions: [] },
  );
  const { rolePermissions, loading } = useSelector(
    (state) =>
      state.assignPermission || { rolePermissions: [], loading: false },
  );

  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchPermissions());
  }, [dispatch]);

  useEffect(() => {
    if (selectedRoleId) {
      dispatch(fetchRolePermissions());
    }
  }, [selectedRoleId, dispatch]);

  useEffect(() => {
    if (rolePermissions.length > 0 && selectedRoleId) {
      const assigned = rolePermissions
        .filter((rp) => rp.role_id === parseInt(selectedRoleId))
        .map((rp) => rp.permission_id);
      setSelectedPermissions(assigned);
    } else {
      setSelectedPermissions([]);
    }
  }, [rolePermissions, selectedRoleId]);

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId],
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRoleId) {
      toast.error("Please select a role");
      return;
    }

    const data = {
      role_id: parseInt(selectedRoleId),
      permissionIds: selectedPermissions,
    };

    try {
      const result = await dispatch(assignRolePermissions(data));
      if (result?.meta?.status) {
        dispatch(fetchRolePermissions());
      }
    } catch (error) {
      // Error handled in action
    }
  };

  const handleRefresh = () => {
    dispatch(fetchRoles());
    dispatch(fetchPermissions());
    if (selectedRoleId) {
      dispatch(fetchRolePermissions());
    }
    toast.success("Data refreshed!");
  };

  return (
    <div className="p-4 md:p-6 w-full">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase text-gray-900 dark:text-white">
            Assign Permissions
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Assign permissions to roles
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
        >
          <FiRefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Select Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Role
            </label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            >
              <option value="">Select a role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.role_name}
                </option>
              ))}
            </select>
          </div>

          {/* Permissions List */}
          {selectedRoleId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Permissions ({selectedPermissions.length} selected)
              </label>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 max-h-60 overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-4 border-orange-600 dark:border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : permissions.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {permissions.map((permission) => (
                      <label
                        key={permission.id}
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissions.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                          className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {permission.permission_name}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                    No permissions available
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Submit Button */}
          {selectedRoleId && (
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <FiCheck className="w-4 h-4" />
                {loading ? "Assigning..." : "Assign Permissions"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AssignPermission;
